import React, { useState, useEffect } from 'react';
import dashboardContext from './dashboardContext';
import dashboardService from '../../services/api/dashboardService';
import { RESTAURANTS } from '../../services/mock/mockDataGenerator';

const DashboardState = (props) => {
  const [selectedRestaurant, setSelectedRestaurant] = useState('REST001');
  const [dateRange, setDateRange] = useState({ days: 7 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Cache for dashboard data
  const [dashboardData, setDashboardData] = useState({
    summary: null,
    salesMetrics: null,
    hourlySales: null,
    topItems: null,
    bottomItems: null,
    salesByChannel: null,
    costMetrics: null,
    costTrend: null,
    wasteData: null,
    operationalMetrics: null,
    serviceTimeTrend: null,
    waiterStats: null,
    chefStats: null,
    staffSchedule: null,
    customerMetrics: null,
    trafficHeatmap: null,
    reviewsTrend: null,
    experiments: null,
    alerts: null,
    restaurantComparison: null
  });

  // Fetch all dashboard data
  const fetchDashboardData = async (restaurantId = selectedRestaurant) => {
    setLoading(true);
    setError(null);

    try {
      const [
        summary,
        salesMetrics,
        hourlySales,
        topItems,
        bottomItems,
        salesByChannel,
        costMetrics,
        costTrend,
        wasteData,
        operationalMetrics,
        serviceTimeTrend,
        waiterStats,
        chefStats,
        staffSchedule,
        customerMetrics,
        trafficHeatmap,
        reviewsTrend,
        experiments,
        alerts
      ] = await Promise.all([
        dashboardService.getDashboardSummary(restaurantId),
        dashboardService.getSalesMetrics(restaurantId, dateRange),
        dashboardService.getHourlySales(restaurantId),
        dashboardService.getTopItems(restaurantId, 10),
        dashboardService.getBottomItems(restaurantId, 5),
        dashboardService.getSalesByChannel(restaurantId),
        dashboardService.getCostMetrics(restaurantId, dateRange),
        dashboardService.getCostTrend(restaurantId, 14),
        dashboardService.getWasteData(restaurantId),
        dashboardService.getOperationalMetrics(restaurantId),
        dashboardService.getServiceTimeTrend(restaurantId, 7),
        dashboardService.getWaiterStats(restaurantId),
        dashboardService.getChefStats(restaurantId),
        dashboardService.getStaffScheduleCompliance(restaurantId),
        dashboardService.getCustomerMetrics(restaurantId),
        dashboardService.getCustomerTrafficHeatmap(restaurantId),
        dashboardService.getReviewsTrend(restaurantId, 30),
        dashboardService.getExperiments(restaurantId),
        dashboardService.getAlerts(restaurantId)
      ]);

      setDashboardData({
        summary: summary.data,
        salesMetrics: salesMetrics.data,
        hourlySales: hourlySales.data,
        topItems: topItems.data,
        bottomItems: bottomItems.data,
        salesByChannel: salesByChannel.data,
        costMetrics: costMetrics.data,
        costTrend: costTrend.data,
        wasteData: wasteData.data,
        operationalMetrics: operationalMetrics.data,
        serviceTimeTrend: serviceTimeTrend.data,
        waiterStats: waiterStats.data,
        chefStats: chefStats.data,
        staffSchedule: staffSchedule.data,
        customerMetrics: customerMetrics.data,
        trafficHeatmap: trafficHeatmap.data,
        reviewsTrend: reviewsTrend.data,
        experiments: experiments.data,
        alerts: alerts.data,
        restaurantComparison: null
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch restaurant comparison (admin only)
  const fetchRestaurantComparison = async () => {
    try {
      const result = await dashboardService.getRestaurantComparison();
      setDashboardData(prev => ({
        ...prev,
        restaurantComparison: result.data
      }));
    } catch (err) {
      console.error('Error fetching restaurant comparison:', err);
    }
  };

  // Change restaurant
  const changeRestaurant = (restaurantId) => {
    setSelectedRestaurant(restaurantId);
    fetchDashboardData(restaurantId);
  };

  // Change date range
  const changeDateRange = (range) => {
    setDateRange(range);
    fetchDashboardData(selectedRestaurant);
  };

  // Manual refresh
  const refreshData = () => {
    fetchDashboardData(selectedRestaurant);
  };

  // Toggle auto refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  // Auto refresh effect
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchDashboardData(selectedRestaurant);
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, selectedRestaurant]);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData(selectedRestaurant);
  }, []);

  return (
    <dashboardContext.Provider
      value={{
        // State
        selectedRestaurant,
        dateRange,
        loading,
        error,
        dashboardData,
        autoRefresh,
        refreshInterval,
        restaurants: RESTAURANTS,

        // Actions
        changeRestaurant,
        changeDateRange,
        refreshData,
        toggleAutoRefresh,
        fetchDashboardData,
        fetchRestaurantComparison,
        setRefreshInterval
      }}
    >
      {props.children}
    </dashboardContext.Provider>
  );
};

export default DashboardState;

